import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { connectGithub } from '@/services/git/api';
import type { GitConnection } from '@/services/git/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Records the Git connection from the GitHub OAuth token the MCP gateway already holds
 * for this user — connected from the Git section or the MCP page, one flow serves both.
 *
 * 401 means the gateway has no usable GitHub token (never authorized, or expired beyond
 * refresh), which is the caller's cue to start the OAuth flow instead.
 */
export function useAdoptGithubConnection(): UseMutationResult<GitConnection, Error, void> {
  const queryClient = useQueryClient();
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  return useMutation({
    mutationFn: () => connectGithub(orgId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gitConnection', orgId] });
      queryClient.invalidateQueries({ queryKey: ['gitRepos', orgId] });
      queryClient.invalidateQueries({ queryKey: ['gitSelectedRepo', orgId] });
    },
  });
}
